import { X } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { useDialogFocusTrap } from '../../hooks/useDialogFocusTrap';
import { useI18n } from '../../i18n/useI18n';
import type {
  Category,
  Transaction,
  TransactionFrequency,
  TransactionType,
  TransactionWrite,
  Wallet,
} from '../../lib/api-types';
import { useUpdateTransaction } from '../transactions/transactions.api';

interface MovementEditorDialogProps {
  readonly categories: readonly Category[];
  readonly onClose: () => void;
  readonly transaction: Transaction;
  readonly wallets: readonly Wallet[];
}

type TransactionDraft = TransactionWrite & {
  active: boolean;
  limitDate: string | null;
  note: string;
  toWalletId: string | null;
};

export function MovementEditorDialog({
  categories,
  onClose,
  transaction,
  wallets,
}: MovementEditorDialogProps) {
  const { t } = useI18n();
  const [draft, setDraft] = useState<TransactionDraft>(() => toDraft(transaction));
  const updateTransaction = useUpdateTransaction();
  const dialogRef = useDialogFocusTrap(onClose);
  const compatibleCategories = categories.filter(
    (category) => draft.type === 'transfer' || category.type === draft.type,
  );
  const canSubmit =
    draft.amount > 0 &&
    draft.categoryId.length > 0 &&
    draft.walletId.length > 0 &&
    (draft.type !== 'transfer' || Boolean(draft.toWalletId && draft.toWalletId !== draft.walletId));

  const setField = <Key extends keyof TransactionDraft>(key: Key, value: TransactionDraft[Key]) =>
    setDraft((current) => ({ ...current, [key]: value }));

  const changeType = (type: TransactionType) => {
    setDraft((current) => ({
      ...current,
      categoryId:
        categories.find((category) => type === 'transfer' || category.type === type)?.id ?? '',
      toWalletId: type === 'transfer' ? current.toWalletId : null,
      type,
    }));
  };

  const changeFrequency = (frequency: TransactionFrequency) => {
    setDraft((current) => ({
      ...current,
      frequency,
      limitDate: frequency === 'one-time' ? null : current.limitDate,
    }));
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await updateTransaction.mutateAsync({ id: transaction.id, input: draft });
    onClose();
  };

  return (
    <div className="dialog-backdrop" role="presentation">
      <section
        aria-labelledby={`edit-movement-${transaction.id}`}
        aria-modal="true"
        className="movement-dialog"
        ref={dialogRef}
        role="dialog"
      >
        <div className="dialog-heading">
          <div>
            <p className="section-kicker">{t('dashboard.myPlan')}</p>
            <h2 id={`edit-movement-${transaction.id}`}>{t('dashboard.editMovement')}</h2>
          </div>
          <button
            aria-label={t('common.close')}
            className="icon-button"
            type="button"
            onClick={onClose}
          >
            <X aria-hidden="true" />
          </button>
        </div>

        <form className="movement-form" onSubmit={submit}>
          <label>
            {t('dashboard.type')}
            <select
              autoFocus
              value={draft.type}
              onChange={(event) => changeType(event.target.value as TransactionType)}
            >
              <option value="expense">{t('dashboard.expense')}</option>
              <option value="income">{t('dashboard.income')}</option>
              <option value="transfer">{t('dashboard.transfer')}</option>
            </select>
          </label>
          <label>
            {t('dashboard.amount')}
            <input
              inputMode="decimal"
              max="1000000000"
              min="0.01"
              required
              step="0.01"
              type="number"
              value={draft.amount}
              onChange={(event) => setField('amount', Number(event.target.value))}
            />
          </label>
          <label>
            {t('dashboard.category')}
            <select
              required
              value={draft.categoryId}
              onChange={(event) => setField('categoryId', event.target.value)}
            >
              {compatibleCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Wallet
            <select
              required
              value={draft.walletId}
              onChange={(event) => {
                const walletId = event.target.value;
                setDraft((current) => ({
                  ...current,
                  toWalletId: current.toWalletId === walletId ? null : current.toWalletId,
                  walletId,
                }));
              }}
            >
              {wallets.map((wallet) => (
                <option key={wallet.id} value={wallet.id}>
                  {wallet.name}
                </option>
              ))}
            </select>
          </label>
          {draft.type === 'transfer' && (
            <label>
              {t('dashboard.destinationWallet')}
              <select
                required
                value={draft.toWalletId ?? ''}
                onChange={(event) => setField('toWalletId', event.target.value || null)}
              >
                <option value="">{t('dashboard.selectDestination')}</option>
                {wallets
                  .filter((wallet) => wallet.id !== draft.walletId)
                  .map((wallet) => (
                    <option key={wallet.id} value={wallet.id}>
                      {wallet.name}
                    </option>
                  ))}
              </select>
            </label>
          )}
          <label>
            {t('dashboard.frequency')}
            <select
              value={draft.frequency}
              onChange={(event) => changeFrequency(event.target.value as TransactionFrequency)}
            >
              <option value="one-time">{t('dashboard.frequencyOneTime')}</option>
              <option value="weekly">{t('dashboard.frequencyWeekly')}</option>
              <option value="biweekly">{t('dashboard.frequencyBiweekly')}</option>
              <option value="monthly">{t('dashboard.frequencyMonthly')}</option>
            </select>
          </label>
          <label>
            {t('dashboard.date')}
            <input
              required
              type="date"
              value={draft.date}
              onChange={(event) => setField('date', event.target.value)}
            />
          </label>
          {draft.frequency !== 'one-time' && (
            <label>
              {t('dashboard.dateLimit')} <span>{t('dashboard.optional')}</span>
              <input
                min={draft.date}
                type="date"
                value={draft.limitDate ?? ''}
                onChange={(event) => setField('limitDate', event.target.value || null)}
              />
            </label>
          )}
          <label className="field-wide">
            {t('dashboard.note')} <span>{t('dashboard.optional')}</span>
            <textarea
              maxLength={500}
              rows={3}
              value={draft.note ?? ''}
              onChange={(event) => setField('note', event.target.value)}
            />
          </label>
          <label className="movement-active-toggle field-wide">
            <input
              checked={draft.active ?? true}
              type="checkbox"
              onChange={(event) => setField('active', event.target.checked)}
            />
            <span>{t('dashboard.activeMovement')}</span>
          </label>

          {updateTransaction.error !== null && (
            <p className="form-error field-wide" role="alert">
              {t('dashboard.saveError')}
            </p>
          )}
          <div className="dialog-actions field-wide">
            <button className="button button-ghost" type="button" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button
              className="button button-primary"
              disabled={!canSubmit || updateTransaction.isPending}
              type="submit"
            >
              {updateTransaction.isPending ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function toDraft(transaction: Transaction): TransactionDraft {
  return {
    active: transaction.active,
    amount: transaction.amount,
    categoryId: transaction.categoryId,
    date: transaction.date,
    frequency: transaction.frequency,
    limitDate: transaction.limitDate,
    note: transaction.note,
    toWalletId: transaction.toWalletId,
    type: transaction.type,
    walletId: transaction.walletId,
  };
}
